import { useParams } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { LeakAlertsWidget } from "@/components/LeakAlertsWidget"
import { LoadingPage } from "@/components/ui/loading-spinner"
import { Calendar, MapPin } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import ExportMenu from "@/components/dashboard/ExportMenu"
import CouncilStats from "./CouncilStats"
import { RecentDevicesWithLeaks } from "./RecentDevicesWithLeaks"
import RecentCustomersRegistered from "./RecentCustomersRegistered"
import useGetDashboard from "@/hooks/api/use-get-dashboard"
import CustomerAnalytics from "@/components/CustomerAnalytics"
import useCurrentUser from "@/hooks/use-current-user"
import { FleetHealthWidget } from "./FleetHealthWidget"
import { ManufacturerFleetHealthWidget } from "./ManufacturerFleetHealthWidget"
import { useTimezone } from "@/hooks/usePreferences"
import useGetAllTimeLeakCount from "@/hooks/api/use-get-all-time-leak-count"

const formatLastSync = (value: string | undefined, timezone: string) => {
  if (!value) return "N/A"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
  const parts = Object.fromEntries(
    formatter.formatToParts(date).map((part) => [part.type, part.value]),
  )
  const formatted = `${parts["year"]}-${parts["month"]}-${parts["day"]} ${parts["hour"]}:${parts["minute"]}:${parts["second"]}`
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  if (timezone === localTimezone) return `${formatted} (Local Time)`

  const offsetPart = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "shortOffset",
  })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value
  const offset = offsetPart?.replace("GMT", "UTC") ?? "UTC"

  return `${formatted} (${offset})`
}

export function CouncilDashboard() {
  const { councilId: urlCouncilId } = useParams()
  const { user } = useAuth()
  const { timezone } = useTimezone()
  const councilId = urlCouncilId || user?.councils?.[0]?.id?.toString()

  const { data, isLoading, isError, error } = useGetDashboard(Number(councilId))
  const { data: totalLeaksDetected } = useGetAllTimeLeakCount(councilId || "")

  const exportData = (type: string, _format: string) => {
    const exportContent = data ? JSON.stringify(data, null, 2) : "{}"

    const mimeType = "application/json;charset=utf-8;"
    const filename = `${type}-export.json`

    const blob = new Blob([exportContent], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
  }
  const { hasRole } = useCurrentUser()

  if (
    hasRole("council_admin") &&
    !user?.councils?.some((c: { id?: number }) => c.id === Number(councilId))
  ) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Access denied</p>
      </div>
    )
  }

  if (isLoading) {
    return <LoadingPage message="Loading council data..." />
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-y-2">
        <p className="text-destructive">Failed to load council data</p>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : "Please try again later"}
        </p>
      </div>
    )
  }

  if (!data?.councilInfo?.name) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Council not found</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in pb-52">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{data.councilInfo.name} Dashboard</h1>
          <p className="text-muted-foreground">Customer portal management and monitoring</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="flex items-center gap-1">
            <MapPin className="size-3" />
            {data.councilInfo.name}
          </Badge>
          <Badge variant="outline" className="flex items-center gap-1">
            <Calendar className="size-3" />
            Last Sync: {formatLastSync(data.stats?.lastSync, timezone)}
          </Badge>
        </div>
      </div>

      {/* Council Statistics */}
      <CouncilStats
        {...(data.stats?.activeDevices !== undefined
          ? { activeDevices: data.stats.activeDevices }
          : {})}
        {...(data.stats?.customers !== undefined ? { customers: data.stats.customers } : {})}
        {...(data.stats?.leakAlarms !== undefined ? { activeLeaks: data.stats.leakAlarms } : {})}
        {...(totalLeaksDetected?.count !== undefined
          ? { totalLeaksDetected: totalLeaksDetected.count }
          : {})}
        {...(data.stats?.devices !== undefined ? { totalDevices: data.stats.devices } : {})}
      />

      {/* Fleet Health */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <FleetHealthWidget
          {...(data.stats?.devices !== undefined ? { totalDevices: data.stats.devices } : {})}
          {...(data.stats?.activeDevices !== undefined
            ? { activeDevices: data.stats.activeDevices }
            : {})}
        />
        <ManufacturerFleetHealthWidget manufacturerHealth={data.manufacturerHealth} />
      </div>

      {/* Analytics Charts */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold mb-2">Leak Analytics</h2>
            <p className="text-sm text-muted-foreground">Real-time leak detection and trends</p>
          </div>
          <ExportMenu type="consumption-analytics" label="Export Charts" onExport={exportData} />
        </div>
        <div className="grid grid-cols-1 gap-6">
          <LeakAlertsWidget />
        </div>
      </div>

      <div className="space-y-4">
        <CustomerAnalytics {...(councilId !== undefined ? { councilId } : {})} />
      </div>

      {/* Data Tables */}
      <div className="space-y-4">
        <div className="flex items-center justify-between pb-5">
          <div>
            <h2 className="text-xl font-semibold mb-2">Recent Activity</h2>
            <p className="text-sm text-muted-foreground">
              Latest device and customer activity overview
            </p>
          </div>
          <ExportMenu type="activity-tables" label="Export Tables" onExport={exportData} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RecentDevicesWithLeaks councilId={councilId!} />
          <RecentCustomersRegistered councilId={councilId ?? ""} />
        </div>
      </div>
    </div>
  )
}
